import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Home, FileText, Bell, Smartphone, MapPin, Upload, Coffee, Check } from 'lucide-react';
import { Button } from './ui/button';

export default function LandingPage() {
  const navigate = useNavigate();
  const [navScrolled, setNavScrolled] = useState(false);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Solidify the nav once the user scrolls past the hero gradient. Threshold
  // is the hero bottom minus the nav height so the swap lands cleanly on the
  // gradient-to-white boundary.
  useEffect(() => {
    const onScroll = () => {
      const hero = document.querySelector('.landing-hero');
      if (!hero) return;
      const threshold = hero.getBoundingClientRect().bottom;
      setNavScrolled(threshold < 80);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="landing-page">
      {/* ───────────── TOP NAV ───────────── */}
      <header
        className={`landing-nav${navScrolled ? ' landing-nav-scrolled' : ''}`}
        data-testid="landing-nav"
      >
        <div className="landing-container landing-nav-inner">
          <div className="landing-nav-brand">
            <img src="/logo-transparent.png" alt="Rental P.A." className="landing-nav-brand-img" />
          </div>
          <div className="landing-nav-actions">
            <button
              className="landing-nav-link"
              onClick={() => navigate('/login')}
              data-testid="landing-nav-login"
            >
              Log In
            </button>
            <Button
              className="btn-primary landing-nav-cta"
              onClick={() => navigate('/signup')}
              data-testid="landing-nav-signup"
            >
              Sign Up
            </Button>
          </div>
        </div>
      </header>

      {/* ───────────── HERO ───────────── */}
      <div className="landing-hero">
        <div className="landing-container">
          <h1 className="landing-title" data-testid="landing-title">
            Never miss a landlord deadline again
          </h1>
          <p className="landing-subtitle" data-testid="landing-subtitle">
            Rental P.A. keeps all your property documents, certificates, and tenancy dates
            in one place — and reminds you before anything expires. Upload a document and
            our AI reads the expiry date for you.
          </p>
          <div className="landing-buttons">
            <Button
              className="btn-primary"
              onClick={() => navigate('/signup')}
              data-testid="hero-cta-signup"
            >
              Start free — 1 property forever
            </Button>
            <Button
              className="btn-secondary"
              onClick={() => scrollTo('how-it-works')}
              data-testid="hero-cta-howitworks"
            >
              See how it works
            </Button>
          </div>
        </div>
      </div>

      {/* ───────────── 3 FEATURE CARDS ───────────── */}
      <div className="landing-features">
        <div className="landing-container">
          <div className="features-grid">
            <div className="feature-card" data-testid="feature-renewals">
              <div className="feature-icon"><Bell size={32} /></div>
              <h3>Never miss a renewal</h3>
              <p>
                Gas Safety, EICR, EPC, insurance — Rental P.A. tracks every expiry date
                and alerts you before they're due. No more spreadsheets, no more surprises.
              </p>
            </div>

            <div className="feature-card" data-testid="feature-ai">
              <div className="feature-icon"><FileText size={32} /></div>
              <h3>AI reads your documents</h3>
              <p>
                Upload a certificate or tenancy agreement and our AI automatically extracts
                the expiry date, premium, tenant details and more. No typing required.
              </p>
            </div>

            <div className="feature-card" data-testid="feature-pocket">
              <div className="feature-icon"><Smartphone size={32} /></div>
              <h3>Everything in one place</h3>
              <p>
                All your properties, all your documents, all your tenant details —
                accessible from your phone wherever you are. Built for landlords managing
                1–5 properties.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ───────────── HOW IT WORKS ───────────── */}
      <section id="how-it-works" className="landing-howitworks" data-testid="howitworks-section">
        <div className="landing-container">
          <h2 className="landing-section-title">How it works</h2>
          <div className="howitworks-steps">
            <div className="howitworks-step" data-testid="step-1">
              <div className="howitworks-step-num">1</div>
              <div className="howitworks-step-icon"><MapPin size={28} /></div>
              <h3>Add your property</h3>
              <p>Enter the address and property type. Rental P.A. automatically creates a compliance checklist for you.</p>
            </div>
            <div className="howitworks-step" data-testid="step-2">
              <div className="howitworks-step-num">2</div>
              <div className="howitworks-step-icon"><Upload size={28} /></div>
              <h3>Upload your documents</h3>
              <p>Gas Safety, EPC, insurance — our AI reads them and sets the expiry dates automatically.</p>
            </div>
            <div className="howitworks-step" data-testid="step-3">
              <div className="howitworks-step-num">3</div>
              <div className="howitworks-step-icon"><Coffee size={28} /></div>
              <h3>Relax</h3>
              <p>We'll remind you when anything needs renewing. Email your tenant about their tenancy end with one tap.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── PRICING ───────────── */}
      <section id="pricing" className="landing-pricing" data-testid="pricing-section">
        <div className="landing-container">
          <h2 className="landing-section-title">Simple, honest pricing</h2>
          <p className="landing-section-subtitle">No credit card required. Cancel any time.</p>

          <div className="pricing-grid">
            {/* Free */}
            <div className="pricing-card" data-testid="pricing-free">
              <div className="pricing-tier-name">Free</div>
              <div className="pricing-headline">1 Property</div>
              <div className="pricing-price pricing-price-promo">
                <span className="price-promo-main">Free for 6 months</span>
                <span className="price-promo-sub">then £1.99/month</span>
              </div>
              <ul className="pricing-features">
                <li><Check size={16} /> All core features</li>
                <li><Check size={16} /> AI document reading (5/month)</li>
                <li><Check size={16} /> Basic email reminders</li>
              </ul>
              <Button
                className="btn-secondary pricing-cta"
                onClick={() => navigate('/signup')}
                data-testid="pricing-cta-free"
              >
                Get started free
              </Button>
            </div>

            {/* Pro */}
            <div className="pricing-card" data-testid="pricing-pro">
              <div className="pricing-tier-name">Pro</div>
              <div className="pricing-headline">Up to 5 Properties</div>
              <div className="pricing-price">
                <span className="price">£4.99</span><span className="period">/month</span>
              </div>
              <div className="pricing-annual">or £39/year</div>
              <ul className="pricing-features">
                <li><Check size={16} /> Everything in Free</li>
                <li><Check size={16} /> Unlimited AI extractions</li>
                <li><Check size={16} /> Weekly digest emails</li>
                <li><Check size={16} /> Tenant renewal emails</li>
              </ul>
              <Button
                className="btn-primary pricing-cta"
                onClick={() => navigate('/signup')}
                data-testid="pricing-cta-pro"
              >
                Start Pro free trial
              </Button>
            </div>

            {/* Max */}
            <div className="pricing-card pricing-card-featured" data-testid="pricing-max">
              <div className="pricing-badge">Most popular for growing portfolios</div>
              <div className="pricing-tier-name">Max</div>
              <div className="pricing-headline">Unlimited Properties</div>
              <div className="pricing-price">
                <span className="price">£9.99</span><span className="period">/month</span>
              </div>
              <div className="pricing-annual">or £79/year</div>
              <ul className="pricing-features">
                <li><Check size={16} /> Everything in Pro</li>
                <li><Check size={16} /> Inbound email document upload</li>
                <li><Check size={16} /> Tenant messaging</li>
                <li><Check size={16} /> Coming soon: bank statement rent checking</li>
              </ul>
              <Button
                className="btn-primary pricing-cta"
                onClick={() => navigate('/signup')}
                data-testid="pricing-cta-max"
              >
                Start Max free trial
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── BOTTOM GRADIENT BOOKEND (CTA + Footer share one gradient) ───────── */}
      <div className="landing-bottom-gradient">
        {/* SECONDARY CTA */}
        <section className="landing-secondary-cta">
          <div className="landing-container">
            <h2>Join landlords who never miss a deadline</h2>
            <p>Free for your first property. No credit card required.</p>
            <Button
              className="btn-primary"
              onClick={() => navigate('/signup')}
              data-testid="footer-cta-signup"
            >
              Get started free →
            </Button>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="landing-footer">
          <div className="landing-container">
            <img
              src="/logo-transparent.png"
              alt="Rental P.A."
              className="landing-footer-logo"
              data-testid="landing-footer-logo"
            />
            <p className="landing-footer-copy">&copy; 2026 Rental P.A. Built for UK landlords.</p>
            <p className="landing-footer-links">
              <Link to="/privacy">Privacy Policy</Link>
              <span className="footer-sep">|</span>
              <Link to="/terms">Terms of Service</Link>
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
